import { Link, useSearchParams } from 'react-router-dom';
import type { Project, ProjectCostSummary } from '@faktorplus/shared';
import { useProjects, useSiteManagers } from '../../api/dictionaries';
import { useCostsByProject } from '../../api/reports';
import { useCurrentUser } from '../../auth/auth-provider';
import { EmptyState, ErrorBox, LoadingBlock } from '../../components/ui/feedback';
import { StatTile, StatTiles } from '../../components/ui/stat-tile';
import { BuildingIcon, CoinsIcon, FileIcon, WalletIcon } from '../../components/ui/icons';
import { formatMoney, formatMoneyCompact } from '../../lib/format';
import { CostShareBar } from './project-charts';
import {
  hasAnyProjectFilter,
  ProjectFiltersBar,
  readProjectFilters,
  type ProjectFilters,
  type ProjectSortKey,
  type ProjectStateFilter,
} from './project-filters';
import pageStyles from '../../components/layout/page.module.css';
import styles from './projects.module.css';

/**
 * The site list — one row per budowa, with what it has cost so far.
 *
 * Both queries are already scoped by the API: a site manager gets their own
 * sites and only the costs booked on them, the office gets everything. The
 * screen therefore has no role branching beyond the "zakończone" toggle, which
 * only the office can act on.
 */
export function ProjectsPage() {
  const user = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = readProjectFilters(searchParams);

  // A site manager only ever sees their own sites, so the office-only filters
  // (finished sites, manager) would be a menu with one answer.
  const isOffice = user.role !== 'SITE_MANAGER';
  const projects = useProjects(isOffice && filters.state !== 'active');
  const managers = useSiteManagers({ enabled: isOffice });
  const costs = useCostsByProject();

  function update(patch: Record<string, string>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    setSearchParams(next, { replace: true });
  }

  if (projects.isLoading) return <LoadingBlock />;
  if (projects.error) return <ErrorBox>Nie udało się pobrać listy budów.</ErrorBox>;

  const hasFilter = hasAnyProjectFilter(searchParams);
  const rows = buildRows(projects.data ?? [], costs.data ?? [], {
    ...filters,
    state: isOffice ? filters.state : 'active',
  });
  const largest = Math.max(...rows.map((row) => row.netAmount), 1);
  const total = rows.reduce((sum, row) => sum + row.netAmount, 0);
  const totalInvoices = rows.reduce((sum, row) => sum + row.invoiceCount, 0);
  const totalContract = rows.reduce((sum, row) => sum + (row.project.contractValue ?? 0), 0);

  return (
    <>
      <header className={pageStyles.header}>
        <h1 className={pageStyles.title}>Budowy</h1>
      </header>

      {rows.length > 0 && (
        <StatTiles>
          <StatTile icon={<BuildingIcon />} label="Budów" value={String(rows.length)} />
          <StatTile
            tone="accent"
            icon={<CoinsIcon />}
            label="Koszty netto"
            value={formatMoneyCompact(total)}
            note={formatMoney(total)}
          />
          <StatTile icon={<FileIcon />} label="Faktury" value={String(totalInvoices)} />
          <StatTile
            icon={<WalletIcon />}
            label="Wartość kontraktów"
            value={formatMoneyCompact(totalContract)}
            note={formatMoney(totalContract)}
          />
        </StatTiles>
      )}

      <ProjectFiltersBar
        params={searchParams}
        isOffice={isOffice}
        managers={managers.data ?? []}
        onChange={update}
        onReset={() => setSearchParams({}, { replace: true })}
      />

      {isOffice && (
        <div className={styles.listBar}>
          <Link to="/budowy/archiwum" className={styles.panelLink}>
            Archiwum budów →
          </Link>
        </div>
      )}

      {/* Costs failing must not read as "these sites cost nothing". */}
      {costs.error && <ErrorBox>Nie udało się pobrać kosztów — kwoty poniżej mogą być niepełne.</ErrorBox>}

      {rows.length === 0 ? (
        <EmptyState
          mark="∅"
          title="Brak budów"
          description={
            hasFilter
              ? 'Żadna budowa nie pasuje do filtrów.'
              : 'Nie masz przypisanej żadnej budowy.'
          }
        />
      ) : (
        <div className={pageStyles.tableWrap}>
          <table className={pageStyles.table}>
            <thead>
              <tr>
                <th>Kod</th>
                <th>Nazwa</th>
                <th className={pageStyles.numeric}>Koszty netto</th>
                <th className={styles.chartColumn}>Udział</th>
                <th className={`${pageStyles.numeric} ${pageStyles.hideOnPhone}`}>Faktury</th>
                <th className={`${pageStyles.numeric} ${pageStyles.hideOnPhone}`}>Kontrakt</th>
                <th className={pageStyles.hideOnPhone}>Stan</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <ProjectRow key={row.project.id} row={row} largest={largest} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

interface ProjectRowData {
  project: Project;
  netAmount: number;
  invoiceCount: number;
}

function ProjectRow({ row, largest }: { row: ProjectRowData; largest: number }) {
  const { project } = row;

  return (
    <tr>
      <td className={pageStyles.docNumber}>
        <Link to={`/budowy/${project.id}`} className={styles.rowLink}>
          {project.code}
        </Link>
      </td>
      <td className={pageStyles.strong}>
        <Link to={`/budowy/${project.id}`} className={styles.rowLink}>
          {project.name}
        </Link>
      </td>
      <td className={pageStyles.numeric}>{formatMoneyCompact(row.netAmount)}</td>
      <td className={styles.chartColumn}>
        <CostShareBar amount={row.netAmount} largest={largest} label={project.name} />
      </td>
      <td className={`${pageStyles.numeric} ${pageStyles.hideOnPhone}`}>{row.invoiceCount}</td>
      <td className={`${pageStyles.numeric} ${pageStyles.hideOnPhone}`}>
        {project.contractValue === null ? '—' : formatMoneyCompact(project.contractValue)}
      </td>
      <td className={pageStyles.hideOnPhone}>
        {project.isActive ? (
          <span className={pageStyles.muted}>W toku</span>
        ) : (
          <span className={styles.finishedFlag}>Zakończona</span>
        )}
      </td>
    </tr>
  );
}

/**
 * Joins the dictionary with the cost report.
 *
 * A site with no invoices yet has no row in the report and must still be
 * listed — it is exactly the site somebody is looking for when they ask why
 * nothing has been booked on it.
 */

function buildRows(
  projects: Project[],
  costs: ProjectCostSummary[],
  filters: ProjectFilters,
): ProjectRowData[] {
  const byProject = new Map(costs.map((cost) => [cost.projectId, cost]));
  const needle = filters.search.trim().toLowerCase();

  return projects
    .filter((project) => matches(project, needle))
    .filter((project) => matchesState(project, filters.state))
    .filter((project) => !filters.managerId || project.managerIds.includes(filters.managerId))
    .map((project) => ({
      project,
      netAmount: byProject.get(project.id)?.netAmount ?? 0,
      invoiceCount: byProject.get(project.id)?.invoiceCount ?? 0,
    }))
    .filter((row) => inCostRange(row.netAmount, filters))
    .sort(comparatorFor(filters.sortBy));
}

function inCostRange(netAmount: number, filters: ProjectFilters): boolean {
  if (filters.costMin !== undefined && netAmount < filters.costMin) return false;
  if (filters.costMax !== undefined && netAmount > filters.costMax) return false;
  return true;
}

function matchesState(project: Project, state: ProjectStateFilter): boolean {
  if (state === 'all') return true;
  return state === 'active' ? project.isActive : !project.isActive;
}

function comparatorFor(sortBy: ProjectSortKey): (a: ProjectRowData, b: ProjectRowData) => number {
  const byName = (a: ProjectRowData, b: ProjectRowData) =>
    a.project.name.localeCompare(b.project.name, 'pl');

  switch (sortBy) {
    case 'name':
      return byName;
    case 'contract':
      return (a, b) =>
        (b.project.contractValue ?? -1) - (a.project.contractValue ?? -1) || byName(a, b);
    case 'invoices':
      return (a, b) => b.invoiceCount - a.invoiceCount || byName(a, b);
    default:
      return (a, b) => b.netAmount - a.netAmount || byName(a, b);
  }
}

function matches(project: Project, needle: string): boolean {
  if (!needle) return true;
  return `${project.code} ${project.name}`.toLowerCase().includes(needle);
}
